import { useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Quagga from '@ericblade/quagga2';

// const MEDIAN_ERROR = 0.11; old value
const MEDIAN_ERROR = 0.30;
const DEBUG_BOX_COLOR = 'rgba(255,221,150,50)' 
const DEBUG_OTHER_BOXES_COLOR = 'rgba(255,214,15,80)';

function getMedian(arr) {
    const newArr = [...arr]; // copy the array before sorting, otherwise it mutates the array passed in, which is generally undesireable
    newArr.sort((a, b) => a - b);
    const half = Math.floor(newArr.length / 2);
    if (newArr.length % 2 === 1) {
        return newArr[half];
    }
    return (newArr[half - 1] + newArr[half]) / 2;
}

function getMedianOfCodeErrors(decodedCodes) {
    const errors = decodedCodes.flatMap(x => x.error);
    const medianOfErrors = getMedian(errors);
    return medianOfErrors;
}

const isAlphanumeric = (str) => {
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    return alphanumericRegex.test(str);
};

const defaultConstraints = {
    width: 1920,
    height: 1110 * window.screen.height / 896,
};

const defaultLocatorSettings = {
    patchSize: 'small',
    halfSample: true,
    willReadFrequently: true,
};

const defaultDecoders = ['code_128_reader', 'code_93_reader'];
// const defaultDecoders = ['code_128_reader', 'code_93_reader', 'ean_reader',
// 'ean_8_reader',
// "code_39_reader",
// "code_39_vin_reader",
// "codabar_reader",
// "upc_reader",
// "upc_e_reader",
// "i2of5_reader",
// "2of5_reader",
// "code_32_reader" ];

const BarcodeScanner = ({
    onDetected,
    scannerRef,
    onScannerReady,
    cameraId,
    setSelectedCameraLabel,
    facingMode,
    constraints = defaultConstraints,
    locator = defaultLocatorSettings,
    decoders = defaultDecoders,
    locate = true,
    zoomLevel
}) => {

    const errorCheck = useCallback((result) => {
        if (!onDetected) {
            return;
        }
        console.log('result', result);
        if (result.codeResult.format === 'code_128') {

            const err = getMedianOfCodeErrors(result.codeResult.decodedCodes);
            // if Quagga is at least 75% certain that it read correctly, then accept the code.
            if (err < MEDIAN_ERROR && isAlphanumeric(result.codeResult.code)) {
                onDetected(result.codeResult.code);
            }
        } else if (result.codeResult.format === 'code_93') {
            if (isAlphanumeric(result.codeResult.code)) {
                onDetected(result.codeResult.code);
            }
        } else {
            onDetected(result.codeResult.code);
        }
    }, [onDetected]);

    const handleProcessed = (result) => {

        
        const drawingCtx = Quagga.canvas.ctx.overlay;
        const drawingCanvas = Quagga.canvas.dom.overlay;
        drawingCtx.font = "24px Arial";
        drawingCtx.fillStyle = 'green';

        drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute('width')), parseInt(drawingCanvas.getAttribute('height')));

        if (result) {
            // console.warn('* quagga onProcessed', result);
            if (result.boxes) {
                drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute('width')), parseInt(drawingCanvas.getAttribute('height')));
                result.boxes.filter((box) => box !== result.box).forEach((box) => {
                    Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, { color: DEBUG_OTHER_BOXES_COLOR, lineWidth: 2 });
                });
            }
            if (result.box) {
                Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: DEBUG_BOX_COLOR, lineWidth: 2 });
            }
            if (result.codeResult && result.codeResult.code) {
                // const validated = barcodeValidator(result.codeResult.code);
                // const validated = validateBarcode(result.codeResult.code);
                // Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, { color: validated ? 'green' : 'red', lineWidth: 3 });
                drawingCtx.font = "24px Arial";
                // drawingCtx.fillStyle = validated ? 'green' : 'red';
                // drawingCtx.fillText(`${result.codeResult.code} valid: ${validated}`, 10, 50);
                drawingCtx.fillText(result.codeResult.code, 10, 20);
                // if (validated) {
                //     onDetected(result);
                // }
            }
        }
    };

    useLayoutEffect(() => {
        // if this component gets unmounted in the same tick that it is mounted, then all hell breaks loose,
        // so we need to wait 1 tick before calling init().  I'm not sure how to fix that, if it's even possible,
        // given the asynchronous nature of the camera functions, the non asynchronous nature of React, and just how
        // awful browsers are at dealing with cameras.
        let ignoreStart = false;
        const init = async () => {
            // wait for one tick to see if we get unmounted before we can possibly even begin cleanup
            await new Promise((resolve) => setTimeout(resolve, 1));
            if (ignoreStart) {
                return;
            }
            // begin scanner initialization
            await Quagga.init({
                inputStream: {
                    type: 'LiveStream',
                    constraints: {
                        ...constraints,
                        ...(cameraId && { deviceId: cameraId }),
                        ...(!cameraId && { facingMode }),
                    },
                    target: scannerRef.current,
                    willReadFrequently: true,
                },
                locator,
                decoder: { readers: decoders },
                locate,
            }, async (err) => {
                Quagga.onProcessed(handleProcessed);

                if (err) {
                    return console.error('Error starting Quagga:', err);
                }
                if (scannerRef && scannerRef.current) {
                    await Quagga.start();
                    const debug = () => {
                        console.log('scannerRef', scannerRef);
                        console.log('cameraId', cameraId);
                        console.log('activeTrack', Quagga.CameraAccess.getActiveTrack());
                        console.log('streamLbabel', Quagga.CameraAccess.getActiveStreamLabel());
                        setSelectedCameraLabel(Quagga.CameraAccess.getActiveStreamLabel());
                        // getCameraById(cameraId);
                    };
                    debug();
                    const constraints = {
                        advanced: [{ zoom: zoomLevel }],
                      };
                    const track = Quagga.CameraAccess.getActiveTrack();
                    track.applyConstraints(constraints)
                    .then(() => {
                    // Do something with the track such as using the Image Capture API.
                    })
                    .catch((e) => {
                    // The constraints could not be satisfied by the available devices.
                    });
                    if (onScannerReady) {
                        onScannerReady();
                        
                    }
                }
            });
            Quagga.onDetected(errorCheck);
        };
        init();
        // cleanup by turning off the camera and any listeners
        return () => {
            ignoreStart = true;
            Quagga.stop();
            Quagga.offDetected(errorCheck);
            Quagga.offProcessed(handleProcessed);
        };
    }, [cameraId, onDetected, onScannerReady, scannerRef, errorCheck, constraints, locator, decoders, locate, facingMode, setSelectedCameraLabel, zoomLevel]);
    return null;
}

BarcodeScanner.propTypes = {
    onDetected: PropTypes.func.isRequired,
    scannerRef: PropTypes.object.isRequired,
    onScannerReady: PropTypes.func,
    cameraId: PropTypes.string,
    facingMode: PropTypes.string,
    constraints: PropTypes.object,
    locator: PropTypes.object,
    decoders: PropTypes.array,
    locate: PropTypes.bool,
};

export default BarcodeScanner;
